import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Coins } from 'lucide-react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';

type LedgerEntry = {
  id: string;
  amount: number;
  balanceAfter: number;
  type:
    | 'ADMIN_GRANT'
    | 'AI_VIDEO_EDIT_USAGE'
    | 'REMOTION_STUDIO_USAGE'
    | 'ANIMATION_STUDIO_EXPORT_USAGE'
    | 'ANIMATION_STUDIO_REFINE_PROMPT_USAGE';
  description: string | null;
  createdAt: string;
  sourceAgentJobId: string | null;
  createdBy: { id: string; fullName: string | null } | null;
  descriptCutJob: { id: string; uploadProjectName: string | null } | null;
};

export default function AgencyAiCredits() {
  const [balance, setBalance] = useState<number | null>(null);
  const [orgName, setOrgName] = useState<string>('');
  const [entries, setEntries] = useState<LedgerEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await api.get<{
          organization: { id: string; name: string } | null;
          balance: number;
          entries: LedgerEntry[];
        }>('/agency/ai-credits/me');
        if (!active) return;
        setBalance(data.balance);
        setOrgName(data.organization?.name || '');
        setEntries(data.entries || []);
      } catch (e: unknown) {
        const err = e as { response?: { data?: { error?: string } } };
        if (!active) return;
        setError(err.response?.data?.error || 'Could not load credits');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="page-title flex items-center gap-2">
          <Coins className="w-8 h-8 text-accent2" />
          AI edit credits
        </h1>
        <p className="text-glass-text-2 text-sm mt-1 max-w-2xl">
          Credits are used for AI video edits and the{' '}
          <Link to="/agency/animation-studio" className="text-accent2 hover:underline">
            Animation studio
          </Link>{' '}
          (each successful edit step or studio generation deducts credits; video edits often range from 10 to 100
          credits per run depending on length and complexity). Contact your platform admin to add credits.
        </p>
      </div>

      {error && <p className="text-red-400 text-sm">{error}</p>}

      <Card className="p-5">
        <CardHeader>
          <CardTitle className="text-glass-text">Balance</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-glass-text-2">Loading…</p>
          ) : (
            <div className="flex flex-wrap items-baseline gap-4">
              <div className="stat-display text-[1.85rem] text-accent2">{balance ?? 0}</div>
              {orgName && <span className="text-glass-text-2 text-sm">for {orgName}</span>}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="p-5">
        <CardHeader>
          <CardTitle className="text-glass-text">Usage &amp; grants</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-glass-text-2">Loading…</p>
          ) : entries.length === 0 ? (
            <p className="text-glass-text-2">No activity yet.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-glass">
              <table className="min-w-full text-sm text-glass-text">
                <thead className="bg-glass-2 border-b border-glass">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">When</th>
                    <th className="text-left px-3 py-2 font-medium">Type</th>
                    <th className="text-right px-3 py-2 font-medium">Change</th>
                    <th className="text-right px-3 py-2 font-medium">Balance after</th>
                    <th className="text-left px-3 py-2 font-medium">Notes</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-glass">
                  {entries.map((row) => (
                    <tr key={row.id} className="hover:bg-glass-2/50">
                      <td className="px-3 py-2 text-glass-text-2 whitespace-nowrap">
                        {new Date(row.createdAt).toLocaleString()}
                      </td>
                      <td className="px-3 py-2">
                        {row.type === 'ADMIN_GRANT'
                          ? 'Credit grant'
                          : row.type === 'REMOTION_STUDIO_USAGE'
                            ? 'Animation studio — generate'
                            : row.type === 'ANIMATION_STUDIO_EXPORT_USAGE'
                              ? 'Animation studio — export'
                              : row.type === 'ANIMATION_STUDIO_REFINE_PROMPT_USAGE'
                                ? 'Animation studio — refine prompt'
                                : 'AI video edit'}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        {row.amount > 0 ? `+${row.amount}` : row.amount}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{row.balanceAfter}</td>
                      <td className="px-3 py-2 max-w-xs">
                        <span className="text-glass-text-2">{row.description}</span>
                        {row.descriptCutJob && (
                          <div className="mt-1">
                            <Link
                              to={`/agency/ai-video-edits/new?jobId=${encodeURIComponent(row.descriptCutJob.id)}`}
                              className="text-accent2 hover:underline text-xs"
                            >
                              View job: {row.descriptCutJob.uploadProjectName || row.descriptCutJob.id.slice(0, 8)}…
                            </Link>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-glass-text-3">
        Monthly allowances and card top-ups can be added later; for now all grants are manual from the admin console.
      </p>
    </div>
  );
}
